import { get, memoize, throttle } from 'lodash';
import moment from 'moment';
import type { ReactNode } from 'react';
import React, { useEffect, useMemo, useState, useRef, Fragment, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { compose } from 'redux';
import { windowWrapper } from '@clubmed/utils';
import { Title } from 'Components/frameworks/title/Title';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import { SearchNoResults } from 'Components/widgets/facet-search-results/search-no-results/SearchNoResults';
import type { Props as SearchResultsProps } from 'Components/widgets/facet-search-results/search-results/SearchResults';
import { SearchResults } from 'Components/widgets/facet-search-results/search-results/SearchResults';
import { filterProductsByFlightDuration } from 'Components/widgets/facet-search-results/search-results/SearchResults.utils';
import type { ProductMappingProps } from 'Components/widgets/product/Product.mapper';
import {
  getProductProps,
  setProductHref,
  updateProduct,
  updateProductUrl,
} from 'Components/widgets/product/Product.mapper';
import type { ProductProps } from 'Components/widgets/product/Product.utils';
import type { ResortGridProps } from 'Components/widgets/products-grid/ProductsGrid';
import 'Components/widgets/products-grid/ProductsGrid';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import {
  changeDepartureCity as changeCity,
  setCustomFilters,
  setInitialProductPrices,
  setProductsBeforeActiveCustomFacetsFiltering,
  setProductsFilteredWithActiveCustomFacets,
} from 'Store/faceting/facetingSearch.action';
import {
  activeCustomFacetsSelector,
  activeFacetsSelector,
  filteredProductsSelector,
  getFiltersSelector,
  recommandedProductsSelector,
} from 'Store/faceting/faceting.selector';
import {
  setProductComparisonSelectionMode,
  toggleProductSelected,
} from 'Store/faceting/productComparison.action';
import { pricesRequestAction, childcareRequestAction } from 'Store/faceting/searchRequest.action';
import { startLoading } from 'Store/global-loader/globalLoader.action';
import { DEFAULT_DEPARTURE_CITY } from 'Store/trip-criteria/departure-city/constants';
import { DEFAULT_TRIP_DURATION } from 'Store/trip-criteria/duration/duration.constants';
import {
  getDepartureCity,
  getDepartureDate,
  getTripDuration,
  getTripFlexibility,
} from 'Store/trip-criteria/tripCriteria.selector';
import { DEFAULT_ADULTS_NB } from 'Store/trip-information/adults/constants';
import { API_DATE_FORMAT, momentToApiDate } from 'Utils/date/apiDate';
import {
  ACTIVITIES_FACET_ID,
  CHILDCARE_FILTER_FACET_ID,
  FLIGHT_DURATION_FACET_ID,
  MAX_NUMBER_OF_SELECTED_PRODUCTS,
} from 'Utils/faceting/constants';
import { getDepartureDateDisplayValue } from 'Utils/faceting/facetingUtils';
import { getFlexibilityDates } from 'Utils/pertimm-client/filters';
import type { ThumbnailProduct } from 'Utils/resort-thumbnail-query/resortThumbnailQuery';
import { sendFacetsToCxO } from 'Utils/seller/sellerUtils';
import { SearchResultsTitle } from './search-results-title/SearchResultsTitle';
export type Props = {
  i18n?: CMSContent;
  initialProducts?: ReadonlyArray<OfferProduct | ProductProps>;
  isProductComparisonModeAuthorized?: boolean;
  offer?: Offer;
  orderByPrice?: 'asc' | 'desc';
  priceSorter?: ReactNode;
  resortGridProps?: ResortGridProps;
  resultsHasSeeMore?: boolean;
  resultsSeeMore?: SearchResultsProps['seeMore'];
  searchOptions?: FacetingSearchOptions;
  shouldSetDefaultDepartureCityOnCompare?: boolean;
  virtualizedGrid?: boolean;
  isSynchronousGrid?: boolean;
  withWorldMap?: boolean;
};

function mapStateToProps(state: ReduxState) {
  const {
    faceting: {
      childcareAvailabilities = [],
      isDateRequired,
      isLoading,
      isPriceLoading,
      isSelectionModeEnabled,
      noResults = false,
      pertimmInterpretations,
      prices = [],
      productsFilteredWithActiveCustomFacets = null,
      productsSelectedForComparison,
      query,
    },
    tripInformation: { adults = [], kids },
  } = state;
  return {
    activeCustomFacets: activeCustomFacetsSelector(state),
    activeFacets: activeFacetsSelector(state),
    adultsNumber: adults.length || DEFAULT_ADULTS_NB,
    childcareAvailabilities,
    departureCity: getDepartureCity(state),
    departureDate: getDepartureDate(state),
    duration: getTripDuration(state),
    filteredProducts: filteredProductsSelector(state),
    filters: getFiltersSelector(state),
    flexibility: getTripFlexibility(state),
    isDateRequired,
    isLoading,
    isPriceLoading,
    isProductComparisonSelectionModeEnabled: isSelectionModeEnabled,
    kids,
    noResults,
    pertimmInterpretations,
    prices,
    productsFilteredWithActiveCustomFacets,
    productsSelectedForComparison,
    query,
    recommandedProducts: recommandedProductsSelector(state),
  };
}

export const FacetSearchResults = ({
  i18n,
  initialProducts,
  isProductComparisonModeAuthorized = false,
  isSynchronousGrid = false,
  orderByPrice,
  priceSorter,
  resortGridProps = {
    displayPrices: true,
  },
  resultsHasSeeMore,
  resultsSeeMore,
  searchOptions = {},
  shouldSetDefaultDepartureCityOnCompare = false,
  virtualizedGrid,
  offer = {},
  withWorldMap = false,
}: Props) => {
  const {
    activeCustomFacets,
    activeFacets,
    adultsNumber,
    childcareAvailabilities,
    departureCity,
    departureDate,
    duration,
    filteredProducts,
    filters,
    flexibility,
    isDateRequired,
    isLoading,
    isPriceLoading,
    isProductComparisonSelectionModeEnabled,
    kids,
    noResults,
    pertimmInterpretations,
    prices,
    productsFilteredWithActiveCustomFacets,
    productsSelectedForComparison,
    query,
    recommandedProducts,
  } = useSelector(mapStateToProps);
  const appContext = useAppContext();
  const [criteriaContext] = useContext(CriteriaContext);
  const filteredProductsRef = useRef(null);
  const dispatch = useDispatch();
  const { isDesktop } = useResponsive();
  // This state, the updateCompareButtonStickyStatus function and the first effect should be moved to SearchCompare
  const [isProductsTopInViewport, setProductsTopInViewport] = useState(true);
  const [isProductsBottomInViewport, setProductsBottomInViewport] = useState(false);
  const { displayFlightDurationFilter } = criteriaContext;

  const getPriceForProduct = (productId: string) =>
    prices.find((p) => p.id === productId)?.price ?? {};

  const getChildcareAvailabilitiesForProduct = (productId: string): Array<any> =>
    childcareAvailabilities.filter(
      (childcareAvailability) => childcareAvailability.productId === productId,
    );

  const flattenResort = (product: ThumbnailProduct): ProductMappingProps => {
    const productId = product.id;
    const isSelectedForComparison = productsSelectedForComparison.includes(productId);
    const maxSelectableItemsReached =
      productsSelectedForComparison.length === MAX_NUMBER_OF_SELECTED_PRODUCTS;
    const price = getPriceForProduct(productId);
    const priceDepartureDate = price?.termsAndConditions?.departureDate;
    let departureDateOn;

    if (
      departureDate &&
      priceDepartureDate &&
      moment(departureDate).format(API_DATE_FORMAT) !== String(priceDepartureDate)
    ) {
      departureDateOn = t('faceting.flexibility.resultIndication', {
        departure_date: moment(priceDepartureDate, API_DATE_FORMAT).format('DD/MM'),
      });
    }

    return {
      comparison: {
        isDisabled:
          isProductComparisonSelectionModeEnabled &&
          !isSelectedForComparison &&
          maxSelectableItemsReached,
        isSelectable:
          isProductComparisonSelectionModeEnabled &&
          (isSelectedForComparison || !maxSelectableItemsReached),
        isSelected: isSelectedForComparison,
      },
      config: i18n,
      displayValidationButton: !appContext.isSeller,
      hasComparison: isProductComparisonSelectionModeEnabled,
      isPriceLoading,
      isSliderDisabled: isProductComparisonSelectionModeEnabled,
      kids,
      offerColor: offer?.color,
      product: {
        ...product,
        childcareAvailabilities: getChildcareAvailabilitiesForProduct(productId),
        departureDateOn,
        price,
      },
    };
  };

  const getProducts = memoize(
    ({
      applyActiveCustomFacets,
      products,
    }: {
      applyActiveCustomFacets: boolean;
      products: Array<ThumbnailProduct>;
    }): {
      filteredProductList: Array<ProductProps>;
      productList: Array<ProductProps>;
    } => {
      const productList = products.map((product) => {
        return compose(updateProductUrl, getProductProps, setProductHref, flattenResort)(product);
      });
      let filteredProductList = productList;

      if (applyActiveCustomFacets) {
        const flightDurationActiveCustomFacets = Object.keys(
          activeCustomFacets[FLIGHT_DURATION_FACET_ID] || {},
        );

        if (flightDurationActiveCustomFacets.length > 0) {
          filteredProductList = [];
          flightDurationActiveCustomFacets.forEach((facet) => {
            filteredProductList = [
              ...filteredProductList,
              ...filterProductsByFlightDuration({
                facet,
                productList,
              }),
            ];
          });
        }
      }

      return {
        filteredProductList,
        productList,
      };
    },
  );

  /**
   * Mutate the products to set the price or the comparison mode
   * We mutate the product to re-render only the subcomponents which needed it
   */
  const getProductWithModifier = ({
    products,
    applyActiveCustomFacets,
  }: {
    applyActiveCustomFacets: boolean;
    products: Array<ThumbnailProduct>;
  }): {
    filteredProductList: Array<ProductProps>;
    productList: Array<ProductProps>;
  } => {
    const { filteredProductList, productList } = getProducts({
      applyActiveCustomFacets,
      products,
    });
    return {
      filteredProductList,
      productList: productList.map<ProductProps>((product, index) =>
        updateProduct(product, flattenResort(products[index])),
      ),
    };
  };

  const filteredProductsLength = filteredProducts?.length ?? 0;
  const displayedProductsCount =
    productsFilteredWithActiveCustomFacets?.length ?? filteredProductsLength;
  const updateCompareButtonStickyStatus = throttle(() => {
    if (displayedProductsCount > 0 && filteredProductsRef.current) {
      const searchResultsWrapperClientRect = filteredProductsRef.current.getBoundingClientRect();
      const clientHeight = windowWrapper.getClientHeight();
      setProductsTopInViewport(searchResultsWrapperClientRect.top <= clientHeight);
      setProductsBottomInViewport(searchResultsWrapperClientRect.bottom <= clientHeight);
    }
  }, 200);
  useEffect(() => {
    if (isProductComparisonModeAuthorized) {
      document.removeEventListener('scroll', updateCompareButtonStickyStatus, false);
      document.addEventListener('scroll', updateCompareButtonStickyStatus, false);
    }

    if (initialProducts && initialProducts.length > 0) {
      dispatch(setInitialProductPrices(initialProducts));
    }
  }, []);
  let productIds = [];
  productIds = useMemo(
    () =>
      (displayedProductsCount > 0 ? filteredProducts : recommandedProducts).map(
        (product) => product.id,
      ),
    [displayedProductsCount > 0 ? filteredProducts : recommandedProducts],
  );
  const childcareActiveFilters = Object.keys(activeFacets[CHILDCARE_FILTER_FACET_ID] ?? {});
  const displaySearchNoResults = displayedProductsCount === 0 && noResults && !isLoading;
  const shouldLoadPrices = resortGridProps.displayPrices || displayFlightDurationFilter;
  useEffect(() => {
    if (!isLoading) {
      if (shouldLoadPrices && productIds && productIds.length > 0) {
        const { minDate, maxDate } = pertimmInterpretations || {};
        const { boutique, defaultDepartureCity, offerId, offerName } = searchOptions;
        const { minDepartureDate, maxDepartureDate } = getFlexibilityDates({
          departureDate,
          flexibility,
        });
        const withFlexibility = flexibility && departureDate;
        const today = moment();
        const { departureDateEnd, departureDateStart } = resortGridProps;
        const start = departureDateStart && moment(departureDateStart);
        const end = departureDateEnd && moment(departureDateEnd);
        const dateStart = start && momentToApiDate(start.isBefore(today) ? today : start);
        const dateEnd = end && momentToApiDate(end);
        dispatch(
          pricesRequestAction({
            appContext,
            criteria: {
              boutique: boutique,
              defaultDepartureCity: defaultDepartureCity || resortGridProps.departureCity,
              departureDate,
              duration: duration || resortGridProps.duration,
              filters,
              maxDate: withFlexibility ? Number(maxDepartureDate) : maxDate || dateEnd,
              minDate: withFlexibility ? Number(minDepartureDate) : minDate || dateStart,
              offerId,
              offerName,
              productIds,
              query: query ? query.label : '',
            },
            withFlexibility,
          }),
        );
      }
    }

    /**
     * Component should fetch childcare availabilities
     * if availabilities don't exist yet
     * and if there are eligeable kids
     */
    const childcareProductIds = childcareAvailabilities.map(
      (childcareAvailability) => childcareAvailability.productId,
    );
    const productsWithoutChildcareAvailabilities = productIds.filter(
      (id) => !childcareProductIds.includes(id),
    );
    const productChildcareAvailabilityNotFetched =
      productsWithoutChildcareAvailabilities.length > 0;
    const kidsWithChildcare = kids.filter((kid) => kid.birthdate);
    const productSortingByChildcareAvailabilitiesRequested =
      childcareActiveFilters.length > 0 || kidsWithChildcare.length > 0;

    if (
      productChildcareAvailabilityNotFetched &&
      productSortingByChildcareAvailabilitiesRequested &&
      departureDate
    ) {
      dispatch(
        childcareRequestAction({
          appContext,
          criteria: {
            childcareActiveFilters,
            departureDate,
            duration: duration || DEFAULT_TRIP_DURATION,
            kids: kidsWithChildcare,
            productIds,
          },
        }),
      );
    }
  }, [isLoading]);
  useEffect(() => {
    if (!isDateRequired && !departureDate) {
      dispatch(setProductComparisonSelectionMode(false));
    }
  }, [isDateRequired]);

  const computeFacetsToCXOParams = () => {
    const destinations = filteredProducts
      .filter((product) => productsSelectedForComparison.indexOf(get(product, 'id')) !== -1)
      .map((destination) => get(destination, 'name'));
    const activities = activeFacets[ACTIVITIES_FACET_ID]
      ? Object.keys(activeFacets[ACTIVITIES_FACET_ID])
      : null;
    return [
      getDepartureDateDisplayValue(departureDate),
      departureCity,
      destinations && destinations.length > 0 ? destinations.join(' / ') : null,
      adultsNumber,
      kids.length,
      activities && activities.length > 0 ? activities.join(' / ') : null, // activities
      adultsNumber + kids.length, // participants
      kids.some((kid) => kid.childcareIncluded),
    ];
  };

  const onClickCompareButton = () => {
    dispatch(startLoading());

    if (appContext.isSeller) {
      sendFacetsToCxO(computeFacetsToCXOParams());
    }
  };

  const toggleProductComparisonSelectionMode = () => {
    dispatch(setProductComparisonSelectionMode(!isProductComparisonSelectionModeEnabled));

    if (
      shouldSetDefaultDepartureCityOnCompare &&
      !departureCity &&
      !isProductComparisonSelectionModeEnabled
    ) {
      dispatch(changeCity(DEFAULT_DEPARTURE_CITY));
    }
  };

  const getCompareUrl = () => {
    // Comparator feature has been removed
    return '';
  };

  const toggleProductSelectedForComparison = () => {
    if (isProductComparisonSelectionModeEnabled) {
      return (productId) => dispatch(toggleProductSelected(productId));
    }
  };

  const comparisonUrl = getCompareUrl();
  const displaySearchResultsPlaceholder = isLoading || (isPriceLoading && shouldLoadPrices);
  const hasDepartureDate = !!departureDate;
  const hasDepartureCity = !!departureCity;
  const shouldDisplayResults = filteredProducts.length > 0 && !isLoading && !noResults;
  const shouldMapResults =
    !displaySearchNoResults && !displaySearchResultsPlaceholder && shouldDisplayResults;
  let filteredResults = [];
  let results = [];
  let noResultsAfterCustomFacetFiltering = false;

  if (shouldMapResults) {
    const { filteredProductList = [], productList = [] } =
      prices.length === 0 && childcareAvailabilities.length === 0
        ? getProducts({
            applyActiveCustomFacets: true,
            products: filteredProducts,
          })
        : getProductWithModifier({
            applyActiveCustomFacets: true,
            products: filteredProducts,
          });
    filteredResults = filteredProductList;
    results = productList;
    noResultsAfterCustomFacetFiltering = filteredResults.length === 0;
  }

  useEffect(() => {
    if (shouldMapResults) {
      dispatch(setProductsFilteredWithActiveCustomFacets(filteredResults));
    }
  }, [JSON.stringify(filteredResults), shouldMapResults]);
  useEffect(() => {
    if (shouldMapResults) {
      dispatch(setProductsBeforeActiveCustomFacetsFiltering(results));
    }
  }, [JSON.stringify(results), shouldMapResults]);
  useEffect(() => {
    const activeCustomFacetIds = Object.keys(activeCustomFacets ?? {});
    const customFacetsFilters = activeCustomFacetIds.reduce((acc1, activeCustomFacetId) => {
      const activeCustomFacet = activeCustomFacets[activeCustomFacetId];

      if (activeCustomFacet) {
        const activeCustomFacetOptionIds = Object.keys(activeCustomFacet ?? {});
        return [
          ...acc1,
          ...activeCustomFacetOptionIds.reduce((acc2, activeCustomFacetOptionId) => {
            const activeCustomFacetOption = activeCustomFacet[activeCustomFacetOptionId];

            if (activeCustomFacetOption) {
              return [
                ...acc2,
                {
                  id: activeCustomFacetId,
                  label: activeCustomFacetOptionId,
                  name: activeCustomFacetOptionId,
                  selected: true,
                },
              ];
            }

            return acc2;
          }, []),
        ];
      }

      return acc1;
    }, []);
    dispatch(setCustomFilters(customFacetsFilters));
  }, [JSON.stringify(activeCustomFacets)]);

  if (displaySearchNoResults || noResultsAfterCustomFacetFiltering) {
    let recommendedResults = [];

    if (recommandedProducts.length > 0) {
      const { productList = [] } =
        prices.length === 0 && childcareAvailabilities.length === 0
          ? getProducts({
              applyActiveCustomFacets: false,
              products: recommandedProducts,
            })
          : getProductWithModifier({
              applyActiveCustomFacets: false,
              products: recommandedProducts,
            });
      recommendedResults = productList;
    }

    const noResultheader = (
      <Title dataTestId="facet-search-results-title" theme="title-sans-n4">
        {t('remote.noResult.recommendation.title')}
      </Title>
    );
    return (
      <Fragment>
        <SearchNoResults
          className="mb-20 lg:mb-60"
          data-testid="facet-search-no-results"
          explanation={t('remote.noResult.updateCriteria')}
          isSeller={appContext.isSeller}
          title={t('remote.noResult.explanation')}
        />
        {recommendedResults.length > 0 && (
          <div data-testid="facet-search-recommanded-results">
            <SearchResults
              childcareActiveFilters={childcareActiveFilters}
              childcareAvailabilities={childcareAvailabilities}
              className="pb-95 lg:pb-40"
              comparisonUrl={comparisonUrl}
              config={i18n}
              displayPlaceholder={displaySearchResultsPlaceholder}
              hasSeeMore={resultsHasSeeMore}
              header={noResultheader}
              isProductsBottomInViewport={isProductsBottomInViewport}
              isProductComparisonSelectionModeEnabled={false}
              isProductComparisonModeAuthorized={false}
              isProductsTopInViewport={isProductsTopInViewport}
              isSynchronousGrid={isSynchronousGrid}
              offer={offer}
              orderByPrice={orderByPrice}
              onClickCompareButton={onClickCompareButton}
              products={recommendedResults}
              toggleProductComparisonSelectionMode={toggleProductComparisonSelectionMode}
              virtualizedGrid={virtualizedGrid}
            />
          </div>
        )}
      </Fragment>
    );
  }

  const resultHeader = (
    <div className="flex items-center justify-between">
      <SearchResultsTitle
        dataTestId="facet-search-results-title"
        pluralLabel={t('faceting.nbResortsThatMatchYourCriteria')}
        resultsCount={filteredResults.length}
        singularLabel={t('faceting.oneResortThatMatchYourCriteria')}
      />
      {priceSorter}
    </div>
  );
  return (
    <div ref={filteredProductsRef} data-testid="facet-search-results">
      <SearchResults
        childcareActiveFilters={childcareActiveFilters}
        className="pb-95 lg:pb-40"
        displayPlaceholder={displaySearchResultsPlaceholder}
        flexibility={flexibility}
        config={i18n}
        header={resultHeader}
        offer={offer}
        orderByPrice={orderByPrice}
        childcareAvailabilities={childcareAvailabilities}
        isProductComparisonSelectionModeEnabled={
          isProductComparisonSelectionModeEnabled &&
          (filteredResults.length > 0 || displayedProductsCount > 0)
        }
        products={filteredResults}
        productsSelectedForComparison={productsSelectedForComparison}
        toggleProductSelectedForComparison={toggleProductSelectedForComparison()}
        toggleProductComparisonSelectionMode={toggleProductComparisonSelectionMode}
        hasSeeMore={resultsHasSeeMore}
        seeMore={resultsSeeMore}
        isSynchronousGrid={isSynchronousGrid}
        kids={kids}
        virtualizedGrid={virtualizedGrid}
        isProductComparisonModeAuthorized={isProductComparisonModeAuthorized}
        isDesktop={isDesktop}
        onClickCompareButton={onClickCompareButton}
        comparisonUrl={comparisonUrl}
        isProductsBottomInViewport={isProductsBottomInViewport}
        isProductsTopInViewport={isProductsTopInViewport}
        hasDepartureDate={hasDepartureDate}
        hasDepartureCity={hasDepartureCity}
        withWorldMap={withWorldMap}
      />
    </div>
  );
};
